import { useBalanceBusinesses } from "@/hooks/api-hooks";
import { Loader, Select } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { setWebTitle } from "@/lib/format-utils";
import SearchInput from "@/components/search-input";
import BalanceTable from "@/components/balance-table";


export default function BalancePage() {
    const { t } = useTranslation();
    setWebTitle(t('balance'));

    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>('all');

    const {
        data: businessesRes,
        isLoading: isLoadingBusinesses
    } = useBalanceBusinesses();

    const combinedGroups = useMemo(() => {
        const groupsMap = new Map<number | string, { id: number | string; name: string; clients: any[] }>();

        const busGroups = businessesRes?.data || [];

        busGroups.forEach(g => {
            groupsMap.set(g.id, {
                id: g.id,
                name: g.name,
                clients: [...(g.clients || [])]
            });
        });
        return Array.from(groupsMap.values());
    }, [businessesRes]);

    const isLoadingBalance = isLoadingBusinesses;

    const filteredGroups = useMemo(() => {
        return combinedGroups.map(group => {
            const filteredClients = group.clients.filter((client: any) => {
                const matchesSearch = searchText ? (
                    (client?.category && client?.category.toLowerCase().includes(searchText.toLowerCase())) ||
                    (client?.name && client?.name.toLowerCase().includes(searchText.toLowerCase())) ||
                    (client?.email && client?.email.toLowerCase().includes(searchText.toLowerCase()))
                ) : true;

                if (!matchesSearch) return false;

                if (statusFilter === 'owes_money') {
                    return Object.values(client.balances || {}).some((b: any) => b.total < -0.05);
                }
                if (statusFilter === 'has_money') {
                    return Object.values(client.balances || {}).some((b: any) => b.total > 0.05);
                }
                if (statusFilter === 'has_sold') {
                    return Object.values(client.balances || {}).some((b: any) => (b.sold || 0) > 0);
                }
                if (statusFilter === 'has_provided') {
                    return Object.values(client.balances || {}).some((b: any) => (b.provided || 0) > 0);
                }
                if (statusFilter === 'has_received') {
                    return Object.values(client.balances || {}).some((b: any) => (b.paid || 0) > 0);
                }
                if (statusFilter === 'has_sent') {
                    return Object.values(client.balances || {}).some((b: any) => (b.payout || 0) > 0);
                }
                return true;
            });

            return {
                ...group,
                clients: filteredClients
            };
        }).filter(group => group.clients.length > 0);
    }, [combinedGroups, searchText, statusFilter]);

    if (isLoadingBalance) {
        return <Loader size="xs" color='black' />;
    }

    return (
        <div>
            <div className="panel-content space-y-4">
                <div className="flex flex-wrap gap-4 mb-4">
                    <Select
                        placeholder={t('filter_by_status')}
                        data={[
                            { value: 'all', label: t('all') },
                            { value: 'owes_money', label: t('owes_money') },
                            { value: 'has_money', label: t('has_money') },
                            { value: 'has_sold', label: t('sold') },
                            { value: 'has_provided', label: t('provider') },
                            { value: 'has_received', label: t('received') },
                            { value: 'has_sent', label: t('sent') },
                        ]}
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val)}
                        allowDeselect={false}
                    />
                    <div className="flex-1">
                        <SearchInput
                            placeholder={t('search')}
                            onSearch={setSearchText}
                        />
                    </div>
                </div>

                {filteredGroups.map((group) => {
                    const businessClients = group.clients.filter(c => c.type === 'business');
                    const personClients = group.clients.filter(c => c.type === 'person');

                    return (
                        <div key={group.id} className="mb-8 bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                            <h2 className="text-sm font-bold text-slate-800 mb-3 pb-1 border-b border-slate-100 uppercase tracking-wider">
                                {group.name}
                            </h2>
                            {businessClients.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">
                                        {t('businesses')}
                                    </h3>
                                    <BalanceTable clients={businessClients} myBusinessName={group.name} />
                                </div>
                            )}

                            {personClients.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">
                                        {t('persons')}
                                    </h3>
                                    <BalanceTable clients={personClients} isPerson myBusinessName={group.name} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
